import React, { useEffect, useState, useCallback } from 'react';
import axios from '../../axios';
import config from '../../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faBolt, faEnvelope, faPhone, faCheck, faTimes, faFeatherAlt } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import styles from './SellerLandingPage.module.css';
import SellerData from '../Seller/SellerData';
import { useLanguage } from '../../i18n/LanguageContext';

function SellerLandingPage() {
  const backend_url = config.backend_url;
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [sellerData, setSellerData] = useState([]);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [phoneInputValue, setPhoneInputValue] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const history = useHistory();
  const { t } = useLanguage();

  // Function to fetch user info from the server
  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${backend_url}/api/user-info`, { 
        withCredentials: true 
      });
      
      const { firstname, lastname, email, phone } = response.data;
      if (firstname || lastname) {
        // Update localStorage
        if (firstname && firstname !== localStorage.getItem('firstname')) {
          localStorage.setItem('firstname', firstname);
        }
        if (lastname && lastname !== localStorage.getItem('lastname')) {
          localStorage.setItem('lastname', lastname);
        }
        
        // Update component state
        const fullName = `${firstname || ''} ${lastname || ''}`.trim();
        setUserName(fullName);
      }
      
      // Set user email and phone
      setUserEmail(email || '');
      setEmailInputValue(email || '');
      setUserPhone(phone || '');
      setPhoneInputValue(phone || '');
    } catch (error) {
      console.error("Error fetching user info:", error);
    } finally {
      setLoading(false);
    }
  }, [backend_url]);

  // Function to update seller contact info (email and phone)
  const updateSellerContact = async (email, phone) => {
    setContactSaving(true);
    setContactError('');
    setContactSuccess(false);
    
    try {
      // Update email if it has changed
      if (email !== userEmail) {
        await axios.put(`${backend_url}/api/update-seller-email`, 
          { email }, 
          { withCredentials: true }
        );
        setUserEmail(email);
      }
      
      // Update phone if it has changed
      if (phone !== userPhone) {
        await axios.put(`${backend_url}/api/update-seller-phone`, 
          { phone }, 
          { withCredentials: true }
        );
        setUserPhone(phone);
      }
      
      setContactSuccess(true);
      setIsEditingContact(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setContactSuccess(false);
      }, 3000);
      
    } catch (error) {
      setContactError(error.response?.data?.error || t('objectForm.validation.unexpected'));
    } finally {
      setContactSaving(false);
    }
  };

  // Handle contact form submission
  const handleContactSubmit = (e) => {
    e.preventDefault();
    const trimmedEmail = emailInputValue.trim();
    const trimmedPhone = phoneInputValue.trim();
    
    // Allow empty values - check if either field has changed (including becoming empty)
    if (trimmedEmail !== userEmail || trimmedPhone !== userPhone) {
      updateSellerContact(trimmedEmail, trimmedPhone);
    } else {
      setIsEditingContact(false);
    }
  };

  // Handle contact edit cancel
  const handleContactCancel = () => {
    setEmailInputValue(userEmail);
    setPhoneInputValue(userPhone);
    setIsEditingContact(false);
    setContactError('');
  };

  // Funktion för att hämta säljarens annonser
  const fetchSellerData = useCallback(async () => {
    try {
      const response = await axios.get(`${backend_url}/api/seller-data`, {
        withCredentials: true,
      });
      setSellerData(response.data);
    } catch (error) {
      console.error('Error fetching seller data:', error);
    }
  }, [backend_url]);

  useEffect(() => {
    // First try to get the name from localStorage
    const firstname = localStorage.getItem('firstname');
    const lastname = localStorage.getItem('lastname');
    
    if (firstname || lastname) {
      const fullName = `${firstname || ''} ${lastname || ''}`.trim();
      setUserName(fullName);
    }
    
    // Always fetch fresh user data from the server
    fetchUserInfo();
    fetchSellerData();
  }, [backend_url, fetchUserInfo, fetchSellerData]);

  // Listen for changes in localStorage
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'firstname' || event.key === 'lastname') {
        const firstname = localStorage.getItem('firstname') || '';
        const lastname = localStorage.getItem('lastname') || '';
        const fullName = `${firstname} ${lastname}`.trim();
        setUserName(fullName);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleCreateObjectWithAI = () => {
    history.push('/object-form?workflow=ai');
  };

  const handleCreateObjectDirect = () => {
    history.push('/object-form?workflow=direct');
  };

  const handleCreateObjectSlim = () => {
    // Use slim workflow and pass the user's email to skip email field
    const emailParam = userEmail ? `&email=${encodeURIComponent(userEmail)}` : '';
    history.push(`/object-form?workflow=slim${emailParam}`);
  };

  const handleDelete = async (dataId) => {
    try {
      await axios.delete(`${backend_url}/api/delete-seller-data/${dataId}`, {
        withCredentials: true,
      });
      
      // Uppdatera listan efter borttagning
      const updatedSellerData = sellerData.filter(item => item.id !== dataId);
      setSellerData(updatedSellerData);
    } catch (error) {
      console.error('Error deleting seller data:', error);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>
        {userName ? t('sellerLanding.welcomeNamed', { name: userName }) : t('sellerLanding.welcome')}
      </h1>
      
      {/* Contact Information Section */}
      <div className={styles.contactSection}>
        <h3>{t('sellerLanding.contactInfo')}</h3>
        {!isEditingContact ? (
          <div className={styles.contactDisplay}>
            <div className={styles.contactInfo}>
              <div className={styles.emailInfo}>
                <FontAwesomeIcon icon={faEnvelope} className={styles.emailIcon} />
                <span className={styles.emailText}>
                  {userEmail && userEmail.trim() !== '' ? userEmail : t('sellerLanding.noEmail')}
                </span>
              </div>
              <div className={styles.phoneInfo}>
                <FontAwesomeIcon icon={faPhone} className={styles.phoneIcon} />
                <span className={styles.phoneText}>
                  {userPhone && userPhone.trim() !== '' ? userPhone : t('sellerLanding.noPhone')}
                </span>
              </div>
            </div>
            <button 
              onClick={() => setIsEditingContact(true)}
              className={styles.editContactButton}
            >
              {t('sellerLanding.editContact')}
            </button>
            {contactSuccess && (
              <div className={styles.successMessage}>
                <FontAwesomeIcon icon={faCheck} />
                {t('sellerLanding.contactUpdated')}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleContactSubmit} className={styles.contactForm}>
            <div className={styles.contactInputContainer}>
              <div className={styles.emailInputContainer}>
                <FontAwesomeIcon icon={faEnvelope} className={styles.emailInputIcon} />
                <input
                  type="email"
                  value={emailInputValue}
                  onChange={(e) => setEmailInputValue(e.target.value)}
                  placeholder={t('sellerLanding.emailPlaceholder')}
                  className={styles.emailInput}
                  disabled={contactSaving}
                />
              </div>
              <div className={styles.phoneInputContainer}>
                <FontAwesomeIcon icon={faPhone} className={styles.phoneInputIcon} />
                <input
                  type="tel"
                  value={phoneInputValue}
                  onChange={(e) => setPhoneInputValue(e.target.value)}
                  placeholder={t('sellerLanding.phonePlaceholder')}
                  className={styles.phoneInput}
                  disabled={contactSaving}
                />
              </div>
            </div>
            {contactError && (
              <div className={styles.errorMessage}>{contactError}</div>
            )}
            <div className={styles.contactButtons}>
              <button 
                type="submit" 
                className={styles.saveContactButton}
                disabled={contactSaving}
              >
                {contactSaving ? t('sellerLanding.saving') : t('sellerLanding.save')}
              </button>
              <button 
                type="button" 
                onClick={handleContactCancel}
                className={styles.cancelContactButton}
                disabled={contactSaving}
              >
                <FontAwesomeIcon icon={faTimes} />
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>

      {sellerData.length === 0 ? (
        <div className={styles.noData}>
          <p><b>{t('sellerLanding.noListings')}</b></p>
          <div className={styles.buttonContainer}>
            <button onClick={handleCreateObjectDirect} className={styles.createButton}>
              <FontAwesomeIcon icon={faBolt} />
              <span>{t('sellerLanding.createWithImages')}</span>
            </button>
            <button onClick={handleCreateObjectSlim} className={styles.createButton}>
              <FontAwesomeIcon icon={faFeatherAlt} />
              <span>{t('sellerLanding.createSimple')}</span>
            </button>
            <button onClick={handleCreateObjectWithAI} className={styles.createButton}>
              <FontAwesomeIcon icon={faRobot} />
              <span>{t('sellerLanding.aiAssistant')}</span>
            </button>
          </div>
          <div className={styles.workflowDescription}>
            <div className={styles.workflowOption}>
              <h4>{t('sellerLanding.withImagesTitle')}</h4>
              <p>{t('sellerLanding.withImagesBody')}</p>
            </div>
            <div className={styles.workflowOption}>
              <h4>{t('sellerLanding.simpleTitle')}</h4>
              <p>{t('sellerLanding.simpleBody')}</p>
            </div>
            <div className={styles.workflowOption}>
              <h4>{t('sellerLanding.aiAssistant')}</h4>
              <p>{t('sellerLanding.aiBody')}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.linkContainer}>
          <div className={styles.buttonContainer}>
            <button onClick={handleCreateObjectDirect} className={styles.createButton}>
              <FontAwesomeIcon icon={faBolt} />
              <span>{t('sellerLanding.createWithImages')}</span>
            </button>
            <button onClick={handleCreateObjectSlim} className={styles.createButton}>
              <FontAwesomeIcon icon={faFeatherAlt} />
              <span>{t('sellerLanding.createSimple')}</span>
            </button>
            <button onClick={handleCreateObjectWithAI} className={styles.createButton}>
              <FontAwesomeIcon icon={faRobot} />
              <span>{t('sellerLanding.aiAssistant')}</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Visa säljarens annonser */}
      {sellerData.length > 0 && (
        <div className={styles.sellerDataWrapper}>
          <h2>{t('sellerLanding.myListings')}</h2>
          <SellerData data={sellerData} onDelete={handleDelete} />
        </div>
      )}
    </div>
  );
}

export default SellerLandingPage;
